<template>
  <div class="container">
    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <i class="fa fa-spinner fa-spin"></i>
      <span>Loading...</span>
    </div>

    <!-- Load failed -->
    <div v-else-if="!dealer" class="loading-container">
      <span>Dealer details could not be loaded.</span>
      <button type="button" class="btn-back" @click="goBack">Back</button>
    </div>

    <div v-else>
      <!-- Header -->
      <div class="promo-section">
        <div class="promo-section-header">
          <h4 class="promo-section-title">Dealer Information</h4>
          <span class="extra-data">Request No: {{ dealer.id }}</span>
        </div>

        <div class="promo-details-box full-width name-status">
          <label>Business Name</label>
          <div class="name-status-row">
            <p>{{ dealer.business_name }}</p>
            <span class="status-badge" :class="statusInfo.class">
              <span class="status-dot"></span>
              {{ statusInfo.text }}
            </span>
          </div>
        </div>

        <div class="promo-details-row">
          <div class="promo-details-box">
            <label>Submitted By</label>
            <p>{{ show(dealer.created_by_name) }}</p>
          </div>
          <div class="promo-details-box">
            <label>Submitted Date & Time</label>
            <p>{{ formatDateTime(dealer.created_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Detail sections -->
      <div v-for="section in sections" :key="section.title" class="promo-section">
        <h4 class="promo-section-title">{{ section.title }}</h4>
        <div class="field-grid">
          <div v-for="field in section.fields" :key="field.label" class="promo-details-box">
            <label>{{ field.label }}</label>
            <p>{{ show(field.value) }}</p>
          </div>
        </div>
      </div>

      <!-- Documents -->
      <div class="promo-section">
        <h4 class="promo-section-title">Documents</h4>
        <div class="doc-grid">
          <div
            v-for="doc in documents"
            :key="doc.key"
            class="doc-card"
            @click="openDocument(doc)"
          >
            <img v-if="isImage(doc.mime)" :src="doc.url" :alt="doc.label" class="doc-image" />
            <div v-else-if="isPdf(doc.mime)" class="doc-icon pdf">
              <i class="fas fa-file-pdf"></i>
              <small>PDF Document</small>
            </div>
            <div v-else class="doc-icon">
              <i class="fas fa-file"></i>
              <small>Click to open</small>
            </div>
            <div class="doc-label">{{ doc.label }}</div>
          </div>
          <p v-if="documents.length === 0" class="muted">No documents uploaded.</p>
        </div>
      </div>

      <!-- Stock -->
      <div class="promo-section">
        <div class="promo-section-header">
          <h4 class="promo-section-title">Initial Stock Levels</h4>
          <span class="extra-data">No of Items: {{ stockItems.length }}</span>
        </div>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item Name</th>
                <th>Min Qty</th>
                <th>Max Qty</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in stockItems" :key="item.item_id">
                <td>{{ item.item_name }}</td>
                <td>{{ item.min_qty }}</td>
                <td>{{ item.qty }}</td>
              </tr>
              <tr v-if="stockItems.length === 0">
                <td colspan="3" class="muted center">No stock items.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Rejected details -->
      <div v-if="Number(dealer.status) === 2" class="promo-section">
        <h4 class="promo-section-title">Rejection Details</h4>
        <div class="promo-details-row">
          <div class="promo-details-box">
            <label>Rejected By</label>
            <p>{{ show(dealer.confirmed_by_name) }}</p>
          </div>
          <div class="promo-details-box">
            <label>Rejected Date & Time</label>
            <p>{{ formatDateTime(dealer.confirmed_at) }}</p>
          </div>
        </div>
        <div class="promo-details-box full-width">
          <label>Rejection Remark</label>
          <p>{{ show(dealer.reject_reason) }}</p>
        </div>
      </div>

      <!-- Approved details -->
      <div v-if="Number(dealer.status) === 1" class="promo-section">
        <h4 class="promo-section-title">Approval Details</h4>
        <div class="promo-details-row">
          <div class="promo-details-box">
            <label>Approved By</label>
            <p>{{ show(dealer.confirmed_by_name) }}</p>
          </div>
          <div class="promo-details-box">
            <label>Approved Date & Time</label>
            <p>{{ formatDateTime(dealer.confirmed_at) }}</p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="form-actions">
        <button type="button" class="btn-back" @click="goBack">Back</button>
        <template v-if="Number(dealer.status) === 0">
          <button type="button" class="btn-reject" @click="openConfirm('reject')">Reject</button>
          <button type="button" class="btn-approve" @click="openConfirm('approve')">Approve</button>
        </template>
      </div>
    </div>

    <!-- Confirm modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="confirm-modal">
        <div class="confirm-icon" :class="currentAction">
          <i :class="currentAction === 'approve' ? 'fa fa-check-circle' : 'fa fa-exclamation-triangle'"></i>
        </div>

        <h4>{{ currentAction === 'approve' ? 'Approve Dealer' : 'Reject Dealer' }}</h4>

        <p v-if="currentAction === 'approve'">
          Are you sure you want to <strong>approve</strong> "{{ dealer?.business_name }}"?
          This will create the dealer account.
        </p>

        <template v-else>
          <p>
            Are you sure you want to <strong>reject</strong> "{{ dealer?.business_name }}"?
            Please give a reason.
          </p>
          <textarea
            v-model="remark"
            class="remark-box"
            rows="3"
            maxlength="500"
            placeholder="Rejection remark (required)"
          ></textarea>
        </template>

        <div class="confirm-actions">
          <button type="button" class="btn-back" @click="closeConfirm" :disabled="submitting">
            Cancel
          </button>
          <button
            type="button"
            :class="currentAction === 'approve' ? 'btn-approve' : 'btn-reject'"
            @click="submitReview"
            :disabled="submitting"
          >
            <i v-if="submitting" class="fa fa-spinner fa-spin"></i>
            {{ submitting ? 'Saving...' : (currentAction === 'approve' ? 'Approve' : 'Reject') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Document preview -->
    <DocumentViewModal
      :show="showDocModal"
      :documentSrc="selectedDoc.url"
      :documentTitle="selectedDoc.label"
      :documentType="selectedDoc.mime"
      @close="showDocModal = false"
    />
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { fetchTempDealerDetails, updateTempDealerStatus } from "@/services/dataService";
import DocumentViewModal from "@/pages/Modal/documents/documentViewModal.vue";

export default {
  name: 'ReviewDealer',
  components: { DocumentViewModal },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      token: localStorage.getItem('token_set'),
      profileId: localStorage.getItem('profile_id'),
      loading: false,
      dealer: null,

      showConfirm: false,
      currentAction: null,
      remark: "",
      submitting: false,

      showDocModal: false,
      selectedDoc: { url: "", label: "", mime: null },
    };
  },

  computed: {
    dealerId() {
        return this.$route.params.id;
    },

    statusInfo() {
      const map = {
        0: { text: "Pending", class: "pending" },
        1: { text: "Approved", class: "approved" },
        2: { text: "Rejected", class: "rejected" },
      };
      return map[Number(this.dealer?.status)] || { text: "Unknown", class: "unknown" };
    },

    stockItems() {
      return this.dealer?.stock_items || [];
    },

    sections() {
      const d = this.dealer;
      if (!d) return [];

      const yesNo = (v) => (Number(v) === 1 ? "Yes" : "No");
      const fullName = [d.title, d.first_name, d.last_name].filter(Boolean).join(" ");

      return [
        {
          title: "Owner Details",
          fields: [
            { label: "Name", value: fullName },
            { label: "Email", value: d.email },
            { label: "Mobile Number", value: d.mobile },
            { label: "Date of Birth", value: d.dob ? String(d.dob).split("T")[0].split(" ")[0] : "" },
            { label: "Identity Type", value: d.id_type },
            { label: "Identity Number", value: d.id_number },
          ],
        },
        {
          title: "Business Details",
          fields: [
            { label: "Company", value: d.company_name },
            { label: "Pricing Category", value: d.dealer_cat_name },
            { label: "Business Type", value: d.dealer_type },
            { label: "Nature of Business", value: d.nature_of_business },
            { label: "Route", value: d.route_name },
            { label: "Business Registration Number", value: d.br_number },
            { label: "Address", value: d.address1 },
            { label: "City", value: d.city },
            { label: "District", value: d.district_name },
            { label: "Postal Code", value: d.post_code },
            { label: "Distributor", value: d.distributor_name },
            { label: "Dealer Group", value: d.dealer_group_name },
            { label: "Sales Executive", value: d.sales_executive_name },
            { label: "Latitude", value: d.latitude },
            { label: "Longitude", value: d.longitude },
            { label: "SAP Code", value: d.sap_code },
            { label: "Home Delivery Available", value: yesNo(d.is_home_delivery) },
            { label: "Eligible for Trust", value: yesNo(d.is_eligible_trust) },
          ],
        },
        {
          title: "Financial Information",
          fields: [
            { label: "VAT No", value: Number(d.vat_liable) === 1 ? d.vat_no : "Not applicable" },
            { label: "VAT Name", value: d.parent_name },
            { label: "VAT Contact No", value: d.parent_contact_no },
            { label: "VAT Address", value: d.parent_address },
            { label: "SVAT No", value: Number(d.svat_liable) === 1 ? d.svat_no : "Not applicable" },
            { label: "Credit Limit Value", value: d.credit_limit },
            { label: "Credit Limit Days", value: d.credit_limit_days },
            { label: "Eligible for Printed Invoice", value: yesNo(d.is_print) },
          ],
        },
      ];
    },

    documents() {
      const d = this.dealer;
      if (!d) return [];

      return [
        { key: "idFront", label: "ID Front", url: d.id_front_url, mime: d.id_front_mime },
        { key: "idBack", label: "ID Back", url: d.id_back_url, mime: d.id_back_mime },
        { key: "br", label: "Business Registration", url: d.br_url, mime: d.br_mime },
        { key: "agreement", label: "Agreement", url: d.agreement_url, mime: d.agreement_mime },
      ].filter((doc) => !!doc.url);
    },
  },

  mounted() {
    this.loadDealer();
  },

  methods: {
    async loadDealer() {
      if (!this.dealerId) {
        this.toast.error("Dealer id is missing.");
        return;
      }

      try {
        this.loading = true;
        this.dealer = null;

        const response = await fetchTempDealerDetails(this.dealerId, this.token);

        if (response.data.status === "success") {
          this.dealer = response.data.data;
        } else {
          this.toast.error(response.data.message || "Failed to load dealer details.");
        }
      } catch (error) {
        console.error("Error loading dealer details:", error);
        this.toast.error(error.response?.data?.message || "Failed to load dealer details.");
      } finally {
        this.loading = false;
      }
    },

    // Approve / reject
    openConfirm(action) {
      this.currentAction = action;
      this.remark = "";
      this.showConfirm = true;
    },

    closeConfirm() {
      if (this.submitting) return;
      this.showConfirm = false;
      this.currentAction = null;
      this.remark = "";
    },

    async submitReview() {
      if (this.currentAction === "reject" && !this.remark.trim()) {
        this.toast.error("Please enter a rejection remark.");
        return;
      }

      try {
        this.submitting = true;

        const payload = {
          action: this.currentAction,
          confirmed_by: Number(this.profileId),
          remark: this.currentAction === "reject" ? this.remark.trim() : null,
        };

        const response = await updateTempDealerStatus(this.dealerId, payload, this.token);

        if (response.data.status === "success") {
          this.toast.success(response.data.message);
          this.showConfirm = false;
          this.goBack();
        } else {
          this.toast.error(response.data.message || "Failed to save the review.");
        }
      } catch (error) {
        console.error("Error reviewing dealer:", error);
        this.toast.error(error.response?.data?.message || "Failed to save the review.");
      } finally {
        this.submitting = false;
      }
    },

    // Documents
    isImage(mime) {
      return !!mime && mime.startsWith("image/");
    },
    isPdf(mime) {
      return mime === "application/pdf";
    },
    openDocument(doc) {
      this.selectedDoc = { url: doc.url, label: doc.label, mime: doc.mime };
      this.showDocModal = true;
    },

    // Helpers
    show(value) {
      return value === null || value === undefined || value === "" ? "—" : value;
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";

      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "Invalid Date";

      const formattedDate = date
        .toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" })
        .replace(/\//g, "-");
      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      return `${formattedDate} | ${formattedTime}`;
    },

    // Back to the Dealer Master tab of the approval console
    goBack() {
      this.$router.push({ path: '/approval-console', query: { tab: 'dealer-master' } });
    },
  },
};
</script>

<style scoped>
.container {
  background-color: #ffffff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 100%;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 16px;
  color: #007bff;
  gap: 10px;
}

.promo-section {
  margin-bottom: 30px;
}

.promo-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.promo-section-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.extra-data {
  font-size: 13px;
  color: grey;
}

.promo-details-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.promo-details-box {
  flex: 1;
  background-color: #f6f6f6;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  min-width: 0;
}

.promo-details-box.full-width {
  margin-bottom: 10px;
}

.promo-details-box label {
  display: block;
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 4px;
  color: #333;
}

.promo-details-box p {
  font-size: 14px;
  margin: 0;
  color: #000;
  word-break: break-word;
  white-space: normal;
}

.name-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.status-badge {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 12px;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-badge.pending { color: #8a6d3b; background-color: #fff3cd; }
.status-badge.pending .status-dot { background-color: #ffb300; }
.status-badge.approved { color: #155724; background-color: #d4edda; }
.status-badge.approved .status-dot { background-color: #28a745; }
.status-badge.rejected { color: #721c24; background-color: #f8d7da; }
.status-badge.rejected .status-dot { background-color: #dc3545; }
.status-badge.unknown { color: #333; background-color: #ddd; }
.status-badge.unknown .status-dot { background-color: #777; }

/* Documents */
.doc-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.doc-card {
  width: 200px;
  cursor: pointer;
  text-align: center;
}

.doc-image,
.doc-icon {
  width: 100%;
  height: 130px;
  border-radius: 8px;
  border: 1px solid #ddd;
  box-sizing: border-box;
  transition: box-shadow 0.2s ease;
}

.doc-image {
  object-fit: cover;
}

.doc-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #6b7280;
}

.doc-icon.pdf {
  background-color: #fef3f2;
  color: #dc2626;
}

.doc-icon i {
  font-size: 2.5rem;
  margin-bottom: 4px;
}

.doc-card:hover .doc-image,
.doc-card:hover .doc-icon {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.doc-label {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

/* Stock table */
.table-wrap {
  width: 100%;
  max-height: 350px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: left;
  border-bottom: 2px solid #ddd;
  background-color: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
  position: sticky;
  top: 0;
}

td {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.muted { color: #9ca3af; }
.center { text-align: center; }

/* Actions */
.form-actions {
  margin-top: 30px;
  padding-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-back,
.btn-reject,
.btn-approve {
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  color: #fff;
  min-width: 100px;
}

.btn-back { background-color: #6c757d; }
.btn-back:hover { background-color: #545b62; }
.btn-reject { background-color: #ef4444; }
.btn-reject:hover { background-color: #dc2626; }
.btn-approve { background-color: #22c55e; }
.btn-approve:hover { background-color: #16a34a; }

.btn-back:disabled,
.btn-reject:disabled,
.btn-approve:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Confirm modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.confirm-modal {
  background: #fff;
  width: 440px;
  max-width: 90%;
  border-radius: 10px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
}

.confirm-icon {
  width: 55px;
  height: 55px;
  margin: 0 auto 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.confirm-icon.approve { background: #e8f7ed; color: #069e27; }
.confirm-icon.reject { background: #fdecec; color: #dc2626; }

.confirm-modal h4 {
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
}

.confirm-modal p {
  margin-bottom: 15px;
  color: #6c757d;
  font-size: 14px;
}

.remark-box {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  margin-bottom: 15px;
}

.remark-box:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .field-grid { grid-template-columns: 1fr; }
  .promo-details-row { flex-direction: column; }
  .doc-card { width: 100%; }
}
</style>